import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon, Input, Switch, Modal, Breadcrumb, NativeSelect } from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";
import { DELIVERY_GROUP_TYPES, getTypeLabel } from "./utils";

import {
  useDeliveryGroupDetail,
  useToggleDeliveryGroupStatus,
  useDeleteDeliveryGroup,
  useUpdateDeliveryGroup,
  useDriverManagers,
  useDrivers,
  useDriverCompanies,
  useBusinessesLite,
} from "../../../hooks";

const DeliveryGroupDetailPage = () => {
  const { t } = useTranslation("delivery");
  const { id } = useParams();
  const groupId = parseInt(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const toggleGroupStatusMutation = useToggleDeliveryGroupStatus();
  const deleteGroupMutation = useDeleteDeliveryGroup();
  const updateGroupMutation = useUpdateDeliveryGroup();

  const {
    data: currentGroup,
    isLoading,
    error,
    isError,
    refetch: refetchGroup,
  } = useDeliveryGroupDetail(groupId);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    administrator_id: "",
    type: 0,
    enabled: true,
    drivers: [],
    driver_companies: [],
    business: [],
  });

  const [errors, setErrors] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [businessSearchInput, setBusinessSearchInput] = useState("");
  const [businessSearchTerm, setBusinessSearchTerm] = useState("");

  // Fetch data for selects
  const { data: driverManagersData } = useDriverManagers("all", "", 1);
  const { data: driversData } = useDrivers(searchTerm);
  const { data: driverCompaniesData } = useDriverCompanies();
  const {
    businesses,
    isLoading: isLoadingBusinesses,
    isError: isBusinessesError,
    refetch: refetchBusinesses,
  } = useBusinessesLite({
    enabled: isEditing,
    searchTerm: businessSearchTerm,
  });

  const driverManagers = driverManagersData?.users || [];
  const drivers = driversData?.drivers || [];
  const driverCompanies = driverCompaniesData?.driverCompanies || [];

  // Selected items state
  const [selectedDrivers, setSelectedDrivers] = useState(new Set());
  const [selectedDriverCompanies, setSelectedDriverCompanies] = useState(
    new Set(),
  );
  const [selectedBusinesses, setSelectedBusinesses] = useState(new Set());

  // Debounce search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBusinessSearchTerm(businessSearchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [businessSearchInput]);

  // Load group data when it's available
  useEffect(() => {
    if (currentGroup && !isEditing) {
      // Handle administrator_id - it can be an object with id or just a number
      const adminId =
        currentGroup.administrator_id?.id ||
        currentGroup.administrator_id ||
        "";

      setFormData({
        name: currentGroup.name || "",
        administrator_id: adminId.toString(),
        type: currentGroup.type ?? DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS,
        enabled: currentGroup.enabled ?? true,
        drivers: [],
        driver_companies: [],
        business: [],
      });

      // Set selected drivers
      if (currentGroup.drivers) {
        if (Array.isArray(currentGroup.drivers)) {
          setSelectedDrivers(
            new Set(currentGroup.drivers.map((d) => d.id || d)),
          );
        } else if (typeof currentGroup.drivers === "string") {
          try {
            const driverIds = JSON.parse(currentGroup.drivers);
            setSelectedDrivers(
              new Set(Array.isArray(driverIds) ? driverIds : []),
            );
          } catch {
            setSelectedDrivers(new Set());
          }
        }
      }

      // Set selected driver companies
      if (
        currentGroup.driver_companies &&
        Array.isArray(currentGroup.driver_companies)
      ) {
        setSelectedDriverCompanies(
          new Set(currentGroup.driver_companies.map((c) => c.id || c)),
        );
      }

      // Set selected businesses
      if (currentGroup.business && Array.isArray(currentGroup.business)) {
        setSelectedBusinesses(
          new Set(currentGroup.business.map((b) => b.id || b)),
        );
      }
    }
  }, [currentGroup, isEditing]);

  const handleToggleGroupStatus = (checked) => {
    // If trying to deactivate (unchecking), show confirmation modal
    if (currentGroup?.enabled && !checked) {
      setShowDeactivateModal(true);
    } else if (!currentGroup?.enabled && checked) {
      // If activating (checking), do it directly
      handleConfirmToggle();
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !currentGroup.enabled;

      await toggleGroupStatusMutation.mutateAsync({
        groupId: currentGroup.id,
        enabled: newEnabledStatus,
      });

      await refetchGroup();

      setShowDeactivateModal(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroupMutation.mutateAsync(currentGroup.id);
      navigate("/delivery/groups");
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleBackToDeliveryGroups = () => {
    navigate("/delivery/groups");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    setErrors({});
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {};

      // Compare name
      if (formData.name !== (currentGroup.name || "")) {
        payload.name = formData.name;
      }

      // Compare administrator_id (handle both object and number formats)
      const currentAdminId =
        currentGroup.administrator_id?.id || currentGroup.administrator_id;
      const formAdminId = formData.administrator_id
        ? parseInt(formData.administrator_id)
        : null;
      if (formAdminId !== currentAdminId) {
        payload.administrator_id = formAdminId;
      }

      // Compare type
      if (formData.type !== (currentGroup.type ?? 0)) {
        payload.type = formData.type;
      }

      // Compare enabled
      if (formData.enabled !== (currentGroup.enabled ?? true)) {
        payload.enabled = formData.enabled;
      }

      // Compare drivers array
      let currentDrivers = [];
      if (currentGroup.drivers) {
        if (Array.isArray(currentGroup.drivers)) {
          currentDrivers = currentGroup.drivers.map((d) => d.id || d).sort();
        } else if (typeof currentGroup.drivers === "string") {
          try {
            const parsed = JSON.parse(currentGroup.drivers);
            currentDrivers = Array.isArray(parsed) ? parsed.sort() : [];
          } catch {
            currentDrivers = [];
          }
        }
      }
      const newDrivers = Array.from(selectedDrivers).sort();
      if (JSON.stringify(currentDrivers) !== JSON.stringify(newDrivers)) {
        payload.drivers = JSON.stringify(newDrivers);
      }

      // Compare driver_companies array
      let currentDriverCompanies = [];
      if (
        currentGroup.driver_companies &&
        Array.isArray(currentGroup.driver_companies)
      ) {
        currentDriverCompanies = currentGroup.driver_companies
          .map((c) => c.id || c)
          .sort();
      }
      const newDriverCompanies = Array.from(selectedDriverCompanies).sort();
      if (
        JSON.stringify(currentDriverCompanies) !==
        JSON.stringify(newDriverCompanies)
      ) {
        payload.driver_companies = newDriverCompanies;
      }

      // Compare business array
      let currentBusinesses = [];
      if (currentGroup.business && Array.isArray(currentGroup.business)) {
        currentBusinesses = currentGroup.business.map((b) => b.id || b).sort();
      }
      const newBusinesses = Array.from(selectedBusinesses).sort();
      if (JSON.stringify(currentBusinesses) !== JSON.stringify(newBusinesses)) {
        payload.business = newBusinesses;
      }

      // If there are no changes, just close editing mode (like cancel)
      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        setErrors({});
        return;
      }

      // Only send the payload if there are actual changes
      await updateGroupMutation.mutateAsync({
        groupId: currentGroup.id,
        data: payload,
      });

      // Invalidate queries to update related components
      // Invalidate driverGroups to update DriverGroupTab
      await queryClient.invalidateQueries({ queryKey: ["driverGroups"] });

      // If administrator_id changed, also invalidate the affected driver managers
      if (payload.administrator_id !== undefined) {
        const oldAdminId =
          currentGroup.administrator_id?.id || currentGroup.administrator_id;
        const newAdminId = payload.administrator_id;

        // Invalidate old administrator's data
        if (oldAdminId) {
          await queryClient.invalidateQueries({
            queryKey: ["driverManager", oldAdminId],
          });
        }

        // Invalidate new administrator's data
        if (newAdminId) {
          await queryClient.invalidateQueries({
            queryKey: ["driverManager", newAdminId],
          });
        }
      }

      setIsEditing(false);
      setErrors({});
      await refetchGroup();
    } catch (error) {
      console.error("Update delivery group error:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    // Reset form data to current group values
    if (currentGroup) {
      // Handle administrator_id - it can be an object with id or just a number
      const adminId =
        currentGroup.administrator_id?.id ||
        currentGroup.administrator_id ||
        "";

      setFormData({
        name: currentGroup.name || "",
        administrator_id: adminId.toString(),
        type: currentGroup.type ?? DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS,
        enabled: currentGroup.enabled ?? true,
        drivers: [],
        driver_companies: [],
        business: [],
      });

      if (currentGroup.drivers) {
        if (Array.isArray(currentGroup.drivers)) {
          setSelectedDrivers(
            new Set(currentGroup.drivers.map((d) => d.id || d)),
          );
        }
      }

      if (
        currentGroup.driver_companies &&
        Array.isArray(currentGroup.driver_companies)
      ) {
        setSelectedDriverCompanies(
          new Set(currentGroup.driver_companies.map((c) => c.id || c)),
        );
      }

      if (currentGroup.business && Array.isArray(currentGroup.business)) {
        setSelectedBusinesses(
          new Set(currentGroup.business.map((b) => b.id || b)),
        );
      }
    }
  };

  const createToggleFunction = (setter) => (id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleDriver = createToggleFunction(setSelectedDrivers);
  const toggleDriverCompany = createToggleFunction(setSelectedDriverCompanies);
  const toggleBusiness = createToggleFunction(setSelectedBusinesses);

  if (isLoading) {
    return (
      <div className="ordering-delivery-surface flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="border-primary mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("groupDetail.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !currentGroup)) {
    return (
      <div className="ordering-delivery-surface px-4 py-8 text-center sm:py-12">
        <Icon
          name="Users"
          className="text-muted-foreground mx-auto mb-3 h-8 w-8 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="text-foreground mt-2 text-sm font-medium sm:text-base">
          {t("groupDetail.states.notFoundTitle")}
        </h3>
        <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
          {error?.message || t("groupDetail.states.notFoundDescription")}
        </p>
        <Button
          emphasis="solid"
          intent="brand"
          size="touch"
          onClick={handleBackToDeliveryGroups}
          className="mt-3 px-3 py-2 transition-colors sm:mt-4 sm:px-4"
        >
          {t("groupDetail.actions.backToGroups")}
        </Button>
      </div>
    );
  }

  const groupName = currentGroup?.name || t("groupDetail.fallbacks.unknown");

  const breadcrumbItems = [
    {
      label: t("groupDetail.breadcrumb.delivery"),
      path: "/delivery/groups",
      icon: "Truck",
    },
    {
      label: groupName,
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="ordering-delivery-surface space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Group Profile Header */}
      <div className="border-border bg-background rounded-lg border">
        <div className="border-border border-b p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="bg-primary text-primary-foreground flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-16 sm:w-16">
                <Icon
                  name="Users"
                  className="h-6 w-6 text-white sm:h-8 sm:w-8"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-foreground truncate text-lg font-bold sm:text-2xl">
                  {groupName}
                </h1>
                <div className="mt-1 flex items-center space-x-2">
                  <Icon name="Tag" className="text-muted-foreground h-4 w-4" />
                  <span className="text-muted-foreground text-sm">
                    {t(`groupTypes.${currentGroup?.type}`, {
                      defaultValue: getTypeLabel(currentGroup?.type),
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              {!isEditing ? (
                <>
                  <Button
                    emphasis="solid"
                    intent="brand"
                    size="touch"
                    onClick={() => setIsEditing(true)}
                    className="flex cursor-pointer items-center justify-center p-2 transition-colors sm:space-x-2 sm:px-4 sm:py-2"
                    title={t("common:actions.edit")}
                  >
                    <Icon name="Edit" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t("common:actions.edit")}
                    </span>
                  </Button>
                  <Button
                    emphasis="solid"
                    intent="danger"
                    size="touch"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={deleteGroupMutation.isPending}
                    className="flex cursor-pointer items-center justify-center p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:space-x-2 sm:px-4 sm:py-2"
                    title={t("common:actions.delete")}
                  >
                    <Icon name="Trash2" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t("common:actions.delete")}
                    </span>
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    emphasis="solid"
                    intent="success"
                    size="touch"
                    onClick={handleSave}
                    disabled={updateGroupMutation.isPending}
                    className="flex cursor-pointer items-center justify-center p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:space-x-2 sm:px-4 sm:py-2"
                    title={t("common:actions.save")}
                  >
                    <Icon name="Check" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {updateGroupMutation.isPending
                        ? t("groupDetail.actions.saving")
                        : t("common:actions.save")}
                    </span>
                  </Button>
                  <Button
                    emphasis="solid"
                    intent="neutral"
                    size="touch"
                    onClick={handleCancel}
                    disabled={updateGroupMutation.isPending}
                    className="flex cursor-pointer items-center justify-center p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:space-x-2 sm:px-4 sm:py-2"
                    title={t("common:actions.cancel")}
                  >
                    <Icon name="X" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t("common:actions.cancel")}
                    </span>
                  </Button>
                </>
              )}
              <div
                className="flex items-center space-x-2"
                title={
                  currentGroup.enabled
                    ? t("groupDetail.actions.deactivate")
                    : t("groupDetail.actions.activate")
                }
              >
                <span
                  className={`text-md font-medium ${
                    currentGroup.enabled ? "text-success" : "text-destructive"
                  }`}
                >
                  {currentGroup.enabled
                    ? t("common:states.enabled")
                    : t("common:states.disabled")}
                </span>
                <Switch
                  checked={!!currentGroup.enabled}
                  onCheckedChange={handleToggleGroupStatus}
                  disabled={toggleGroupStatusMutation.isPending}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
          {/* Basic Information */}
          <div className="border-border bg-card rounded-lg border p-4 sm:p-6">
            <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
              <Icon name="Info" className="mr-2 h-5 w-5" />
              {t("groupDetail.sections.basicInformation")}
            </h3>

            {isEditing ? (
              <div className="space-y-4">
                <Input
                  label={t("groupDetail.fields.name.label")}
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={errors.name}
                  placeholder={t("groupDetail.fields.name.placeholder")}
                  required
                />

                <div>
                  <label
                    htmlFor="delivery-group-detail-administrator"
                    className="text-foreground mb-2 block text-sm font-medium"
                  >
                    {t("groupDetail.fields.deliveryManager.label")}{" "}
                    <span className="text-destructive">*</span>
                  </label>
                  <NativeSelect
                    id="delivery-group-detail-administrator"
                    value={formData.administrator_id}
                    onChange={(e) =>
                      handleInputChange("administrator_id", e.target.value)
                    }
                    className={`bg-secondary w-full rounded-lg border px-3 py-2 text-white ${
                      errors.administrator_id
                        ? "border-destructive"
                        : "border-border"
                    } custom-select focus:ring-ring focus:ring-2 focus:outline-none`}
                    aria-invalid={Boolean(errors.administrator_id)}
                    aria-describedby={
                      errors.administrator_id
                        ? "delivery-group-detail-administrator-error"
                        : undefined
                    }
                  >
                    <option value="">
                      {t(
                        "groupDetail.fields.deliveryManager.selectPlaceholder",
                      )}
                    </option>
                    {driverManagers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name} {manager.lastname}{" "}
                        {manager.email ? `(${manager.email})` : ""}
                      </option>
                    ))}
                  </NativeSelect>
                  {errors.administrator_id && (
                    <p
                      id="delivery-group-detail-administrator-error"
                      className="text-destructive mt-1 text-xs"
                    >
                      {errors.administrator_id}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="delivery-group-detail-type"
                    className="text-foreground mb-2 block text-sm font-medium"
                  >
                    {t("groupDetail.fields.type.label")}{" "}
                    <span className="text-destructive">*</span>
                  </label>
                  <NativeSelect
                    id="delivery-group-detail-type"
                    value={formData.type}
                    onChange={(e) =>
                      handleInputChange("type", parseInt(e.target.value))
                    }
                    className="custom-select border-border bg-secondary focus:ring-ring w-full rounded-lg border px-3 py-2 text-white focus:ring-2 focus:outline-none"
                  >
                    <option value={DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS}>
                      {t("groupTypes.inHouseDrivers")}
                    </option>
                    <option value={DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES}>
                      {t("groupTypes.deliveryCompanies")}
                    </option>
                  </NativeSelect>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("groupDetail.summary.name")}
                  </span>
                  <span className="text-foreground">
                    {currentGroup?.name ||
                      t("groupDetail.fallbacks.notAvailable")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("groupDetail.summary.type")}
                  </span>
                  <span className="text-foreground">
                    {t(`groupTypes.${currentGroup?.type}`, {
                      defaultValue: getTypeLabel(currentGroup?.type),
                    })}
                  </span>
                </div>
                {currentGroup?.administrators &&
                  currentGroup.administrators.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t("groupDetail.summary.deliveryManager")}
                      </span>
                      <span className="text-foreground">
                        {currentGroup.administrators[0].name}{" "}
                        {currentGroup.administrators[0].lastname}
                      </span>
                    </div>
                  )}
                {currentGroup?.created_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("groupDetail.summary.created")}
                    </span>
                    <span className="text-foreground">
                      {new Date(currentGroup.created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Drivers Selection (when type = 0) */}
          {formData.type === DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS && (
            <div className="border-border bg-card rounded-lg border p-4 sm:p-6">
              <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
                <Icon name="Users" className="mr-2 h-5 w-5" />
                {t("groupDetail.sections.drivers")}
              </h3>

              {isEditing ? (
                <div className="space-y-4">
                  <Input
                    label={t("groupDetail.fields.searchDrivers.label")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t(
                      "groupDetail.fields.searchDrivers.placeholder",
                    )}
                  />

                  <div className="border-border bg-secondary max-h-60 overflow-y-auto rounded-lg border p-4">
                    {drivers.length === 0 ? (
                      <p className="text-muted-foreground py-4 text-center text-sm">
                        {t("groupDetail.states.noDriversFound")}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {drivers.map((driver) => {
                          const name =
                            driver.name && driver.lastname
                              ? `${driver.name} ${driver.lastname}`
                              : driver.name ||
                                t("groupDetail.fallbacks.unknown");
                          const isSelected = selectedDrivers.has(driver.id);

                          return (
                            <div
                              key={driver.id}
                              className={`flex items-center justify-between rounded-lg border p-3 ${
                                isSelected
                                  ? "border-primary bg-primary/10"
                                  : "border-border bg-muted/50"
                              }`}
                            >
                              <div className="flex flex-1 items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleDriver(driver.id)}
                                  className="border-border bg-muted text-primary focus:ring-ring h-4 w-4 rounded"
                                />
                                {driver.photo ? (
                                  <img
                                    src={driver.photo}
                                    alt={name}
                                    className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextSibling.style.display =
                                        "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`bg-muted flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                                    driver.photo ? "hidden" : ""
                                  }`}
                                >
                                  <Icon
                                    name="User"
                                    className="text-muted-foreground h-4 w-4"
                                  />
                                </div>
                                <div>
                                  <p className="text-foreground text-sm font-medium">
                                    {name}
                                  </p>
                                  {driver.email && (
                                    <p className="text-muted-foreground text-xs">
                                      {driver.email}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.drivers && (
                    <p className="text-destructive text-xs">{errors.drivers}</p>
                  )}
                </div>
              ) : (
                <div className="border-border bg-secondary max-h-60 overflow-y-auto rounded-lg border p-4">
                  {currentGroup?.drivers &&
                  Array.isArray(currentGroup.drivers) &&
                  currentGroup.drivers.length > 0 ? (
                    <div className="space-y-2">
                      {currentGroup.drivers.map((driver) => {
                        const name =
                          driver.name && driver.lastname
                            ? `${driver.name} ${driver.lastname}`
                            : driver.name || t("groupDetail.fallbacks.unknown");
                        return (
                          <div
                            key={driver.id || driver}
                            className="border-border bg-muted flex items-center space-x-3 rounded-lg border p-3"
                          >
                            {driver.photo ? (
                              <img
                                src={driver.photo}
                                alt={name}
                                className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className={`bg-muted flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                                driver.photo ? "hidden" : ""
                              }`}
                            >
                              <Icon
                                name="User"
                                className="text-muted-foreground h-5 w-5"
                              />
                            </div>
                            <div>
                              <p className="text-foreground text-sm">{name}</p>
                              {driver.email && (
                                <p className="text-muted-foreground mt-1 text-xs">
                                  {driver.email}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("groupDetail.states.noDriversAssigned")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Driver Companies Selection (when type = 1) */}
          {formData.type === DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES && (
            <div className="border-border bg-card space-y-4 rounded-lg border p-4 sm:p-6">
              <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
                <Icon name="Truck" className="mr-2 h-5 w-5" />
                {t("groupDetail.sections.deliveryCompanies")}
              </h3>

              {isEditing ? (
                <div className="space-y-4">
                  <div className="border-border bg-secondary max-h-60 overflow-y-auto rounded-lg border p-4">
                    {driverCompanies.length === 0 ? (
                      <p className="text-muted-foreground py-4 text-center text-sm">
                        {t("groupDetail.states.noDeliveryCompaniesFound")}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {driverCompanies.map((company) => {
                          const isSelected = selectedDriverCompanies.has(
                            company.id,
                          );

                          return (
                            <div
                              key={company.id}
                              className={`flex items-center justify-between rounded-lg border p-3 ${
                                isSelected
                                  ? "border-primary bg-primary/10"
                                  : "border-border bg-muted/50"
                              }`}
                            >
                              <div className="flex flex-1 items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    toggleDriverCompany(company.id)
                                  }
                                  className="border-border bg-muted text-primary focus:ring-ring h-4 w-4 rounded"
                                />
                                <div>
                                  <p className="text-foreground text-sm font-medium">
                                    {company.name ||
                                      t("groupDetail.fallbacks.companyWithId", {
                                        id: company.id,
                                      })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.driver_companies && (
                    <p className="text-destructive text-xs">
                      {errors.driver_companies}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {currentGroup?.driver_companies &&
                  Array.isArray(currentGroup.driver_companies) &&
                  currentGroup.driver_companies.length > 0 ? (
                    currentGroup.driver_companies.map((company) => (
                      <div
                        key={company.id || company}
                        className="border-border bg-secondary rounded-lg border p-3"
                      >
                        <p className="text-foreground text-sm">
                          {company.name ||
                            t("groupDetail.fallbacks.companyWithId", {
                              id: company.id || company,
                            })}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t("groupDetail.states.noDeliveryCompaniesAssigned")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Businesses Selection */}
          <div className="border-border bg-card space-y-4 rounded-lg border p-4 sm:p-6">
            <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
              <Icon name="Store" className="mr-2 h-5 w-5" />
              {t("groupDetail.sections.businesses")}
            </h3>

            {isEditing ? (
              <div className="space-y-4">
                <Input
                  label={t("groupDetail.fields.searchBusinesses.label")}
                  value={businessSearchInput}
                  onChange={(e) => setBusinessSearchInput(e.target.value)}
                  placeholder={t(
                    "groupDetail.fields.searchBusinesses.placeholder",
                  )}
                />

                <div className="border-border bg-secondary max-h-60 overflow-y-auto rounded-lg border p-4">
                  {isLoadingBusinesses ? (
                    <div className="py-4 text-center">
                      <div className="border-primary mx-auto h-6 w-6 animate-spin rounded-full border-b-2"></div>
                      <p className="text-muted-foreground mt-2 text-sm">
                        {t("groupDetail.states.loadingBusinesses")}
                      </p>
                    </div>
                  ) : isBusinessesError ? (
                    <div role="alert" className="space-y-2 py-4 text-center">
                      <p className="text-destructive text-sm">
                        {t("common:states.error")}
                      </p>
                      <Button
                        type="button"
                        emphasis="outline"
                        intent="neutral"
                        size="touch"
                        onClick={() => refetchBusinesses()}
                      >
                        {t("common:actions.retry")}
                      </Button>
                    </div>
                  ) : businesses.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("groupDetail.states.noBusinessesFound")}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {businesses.map((business) => {
                        const isSelected = selectedBusinesses.has(business.id);

                        return (
                          <div
                            key={business.id}
                            className={`flex items-center justify-between rounded-lg border p-3 ${
                              isSelected
                                ? "border-primary bg-primary/10"
                                : "border-border bg-muted/50"
                            }`}
                          >
                            <div className="flex flex-1 items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleBusiness(business.id)}
                                className="border-border bg-muted text-primary focus:ring-ring h-4 w-4 rounded"
                              />
                              {business.logo ? (
                                <img
                                  src={business.logo}
                                  alt={business.name}
                                  className="h-8 w-8 rounded object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="bg-muted flex h-8 w-8 items-center justify-center rounded">
                                  <Icon
                                    name="Store"
                                    className="text-muted-foreground h-4 w-4"
                                  />
                                </div>
                              )}
                              <div>
                                <p className="text-foreground text-sm font-medium">
                                  {business.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {errors.business && (
                  <p className="text-destructive text-xs">{errors.business}</p>
                )}
              </div>
            ) : (
              <div className="border-border bg-secondary max-h-70 overflow-y-auto rounded-lg border p-4">
                {currentGroup?.business &&
                Array.isArray(currentGroup.business) &&
                currentGroup.business.length > 0 ? (
                  <div className="space-y-2">
                    {currentGroup.business.map((business) => (
                      <div
                        key={business.id || business}
                        className="border-border bg-muted flex items-center space-x-3 rounded-lg border p-3"
                      >
                        {business.logo ? (
                          <img
                            src={business.logo}
                            alt={business.name}
                            className="h-10 w-10 flex-shrink-0 rounded object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="bg-muted flex h-10 w-10 flex-shrink-0 items-center justify-center rounded">
                            <Icon
                              name="Store"
                              className="text-muted-foreground h-5 w-5"
                            />
                          </div>
                        )}
                        <div>
                          <p className="text-foreground text-sm font-medium">
                            {business.name ||
                              t("groupDetail.fallbacks.businessWithId", {
                                id: business.id || business,
                              })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    {t("groupDetail.states.noBusinessesAssigned")}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deactivate Group Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title={t("groupDetail.deactivateModal.title")}
        subtitle={t("groupDetail.deactivateModal.subtitle")}
        maxWidth="max-w-md"
        className="ordering-delivery-modal"
      >
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="bg-destructive/20 flex h-8 w-8 items-center justify-center rounded-full sm:h-10 sm:w-10">
                <Icon
                  name="AlertTriangle"
                  className="text-destructive h-4 w-4 sm:h-5 sm:w-5"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-foreground text-sm font-medium">
                {t("groupDetail.deactivateModal.confirmTitle", {
                  name: groupName,
                })}
              </h3>
              <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                {t("groupDetail.deactivateModal.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <Button
              emphasis="solid"
              intent="danger"
              size="touch"
              onClick={handleConfirmToggle}
              disabled={toggleGroupStatusMutation.isPending}
              className="flex flex-1 items-center justify-center space-x-2 px-3 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
            >
              {toggleGroupStatusMutation.isPending ? (
                <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
              ) : null}
              <span>
                {toggleGroupStatusMutation.isPending
                  ? t("groupDetail.deactivateModal.deactivating")
                  : t("groupDetail.actions.deactivate")}
              </span>
            </Button>
            <Button
              emphasis="solid"
              intent="neutral"
              size="touch"
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleGroupStatusMutation.isPending}
              className="text-foreground flex-1 px-3 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
            >
              {t("common:actions.cancel")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t("groupDetail.deleteModal.title")}
        itemName={groupName}
        itemType={t("groupDetail.deleteModal.itemType")}
        isLoading={deleteGroupMutation.isPending}
      />
    </div>
  );
};

export default DeliveryGroupDetailPage;
